import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useOptionalProject } from "@/components/project-context";
import { Page } from "@/components/layout/page";
import { trpc } from "@/lib/trpc";
import { authedFetch } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Copy,
  ExternalLink,
  Globe,
  User,
  ClipboardCheck,
  Palette,
  Info,
} from "lucide-react";
import { toast } from "sonner";
import { features } from "@/lib/features";

interface PrepareResult {
  walkthroughId: string;
  promptText: string;
  knowledgeItemCount: number;
  targetUrl: string;
  enabledLenses: string[];
}

type LensId = "persona" | "domain_expert" | "designer";

interface LensOption {
  id: LensId;
  label: string;
  description: string;
  icon: typeof User;
  alwaysEnabled?: boolean;
}

const LENS_OPTIONS: LensOption[] = [
  {
    id: "persona",
    label: "Persona",
    description: "Pure usability testing through the persona's eyes",
    icon: User,
    alwaysEnabled: true,
  },
  ...(features.knowledgeSources
    ? [
        {
          id: "domain_expert" as const,
          label: "Domain Expert",
          description: "Compliance checking against your domain sources",
          icon: ClipboardCheck,
        },
      ]
    : []),
  {
    id: "designer",
    label: "Designer",
    description: "Design consistency against project design references",
    icon: Palette,
  },
];

export function ProjectLiveWalkthroughPage() {
  const { id: projectId } = useParams<{ id: string }>();
  if (!projectId) return null;

  return (
    <Page
      title="Live walkthrough"
      back={{
        to: `/projects/${projectId}/walkthroughs`,
        label: "Walkthroughs",
      }}
    >
      <LiveWalkthroughForm projectId={projectId} />
    </Page>
  );
}

function LiveWalkthroughForm({ projectId }: { projectId: string }) {
  const [searchParams] = useSearchParams();
  const project = useOptionalProject();
  const [personaId, setPersonaId] = useState(
    searchParams.get("personaId") ?? searchParams.get("persona") ?? "",
  );
  const [scenarioId, setScenarioId] = useState(
    searchParams.get("scenarioId") ?? searchParams.get("scenario") ?? "",
  );
  const [targetUrl, setTargetUrl] = useState(
    searchParams.get("targetUrl") ?? project?.productUrl ?? "",
  );
  const [selectedLenses, setSelectedLenses] = useState<LensId[]>(["persona"]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<PrepareResult | null>(null);

  const { data: personas } = trpc.persona.list.useQuery({ projectId });
  const { data: scenarios } = trpc.scenario.list.useQuery({ projectId });
  const { data: lensAvailability } = trpc.walkthrough.lensAvailability.useQuery(
    { projectId },
  );

  const handleStart = async () => {
    if (!personaId || !scenarioId || !targetUrl) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      const res = await authedFetch("/api/walkthroughs/prepare", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scenarioId,
          personaId,
          targetUrl,
          lenses: selectedLenses,
        }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error ?? "Failed to prepare walkthrough");
      }

      const data: PrepareResult = await res.json();
      setResult(data);
      toast.success("Walkthrough prepared");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to prepare walkthrough",
      );
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  if (result) {
    const mcpCommand = `Use the Iamu MCP tools to walk through ${result.targetUrl} as the persona. The walkthrough ID is ${result.walkthroughId}. Use record_step (with screenshots via take_screenshot) and record_finding to document your observations, then call complete_walkthrough when done.`;

    return (
      <div className="space-y-6">
        <div className="rounded-xl border border-border bg-card p-6">
          <div className="mb-4 flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-mark-helps/10 text-finding-helps">
              <Globe className="h-4 w-4" aria-hidden="true" />
            </div>
            <h2 className="text-lg font-semibold">
              Walkthrough instructions ready
            </h2>
          </div>

          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2">
              <span className="text-muted-foreground">Walkthrough ID</span>
              <div className="flex items-center gap-2">
                <code className="font-mono text-xs">
                  {result.walkthroughId}
                </code>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() =>
                    copyToClipboard(result.walkthroughId, "Walkthrough ID")
                  }
                >
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
            </div>

            <div className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2">
              <span className="text-muted-foreground">Target URL</span>
              {(() => {
                let isLocal = false;
                try {
                  const parsed = new URL(result.targetUrl);
                  isLocal =
                    ["localhost", "127.0.0.1", "0.0.0.0", "::1"].includes(
                      parsed.hostname,
                    ) || parsed.hostname.endsWith(".local");
                } catch {
                  isLocal = false;
                }

                if (isLocal) {
                  return (
                    <div className="flex items-center gap-1.5">
                      <span className="font-mono text-xs text-muted-foreground">
                        {result.targetUrl}
                      </span>
                      <span className="rounded bg-muted px-1.5 py-0.5 text-xs font-medium text-muted-foreground">
                        Local
                      </span>
                    </div>
                  );
                }

                return (
                  <a
                    href={result.targetUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-primary hover:underline"
                  >
                    {result.targetUrl}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                );
              })()}
            </div>

            <div className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2">
              <span className="text-muted-foreground">Knowledge items</span>
              <span>{result.knowledgeItemCount}</span>
            </div>
          </div>
        </div>

        {/* Agent Instructions */}
        <div className="rounded-xl border border-border bg-card p-6">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-md font-semibold">Agent instructions</h3>
            <Button
              variant="outline"
              size="sm"
              onClick={() => copyToClipboard(mcpCommand, "Instructions")}
            >
              <Copy className="mr-2 h-4 w-4" />
              Copy
            </Button>
          </div>
          <p className="mb-3 text-sm text-muted-foreground">
            Copy these instructions and paste them into your AI agent (Claude
            Code, Cursor, etc.) to start the walkthrough:
          </p>
          <pre className="whitespace-pre-wrap rounded-lg bg-muted/50 p-4 font-mono text-xs leading-relaxed">
            {mcpCommand}
          </pre>
        </div>

        <Button variant="outline" onClick={() => setResult(null)}>
          Prepare another walkthrough
        </Button>
      </div>
    );
  }

  const missingPersonas = personas !== undefined && personas.length === 0;
  const missingScenarios = scenarios !== undefined && scenarios.length === 0;

  return (
    <div className="max-w-lg space-y-6">
      <p className="text-sm text-muted-foreground">
        Set up a live walkthrough where an AI agent navigates a real website as
        a domain-grounded persona, recording observations and findings along the
        way.
      </p>

      {(missingPersonas || missingScenarios) && (
        <div className="rounded-lg border border-border bg-muted/40 p-4 space-y-3">
          <div className="flex items-start gap-2.5">
            <Info className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="text-sm font-medium">Prerequisites needed</p>
              <p className="text-xs text-muted-foreground">
                Live walkthroughs require at least one persona to drive the
                browser and one scenario to define the goal.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 pl-6.5">
            {missingPersonas && (
              <Button variant="outline" size="sm" asChild>
                <Link to={`/projects/${projectId}/personas`}>
                  Create persona
                </Link>
              </Button>
            )}
            {missingScenarios && (
              <Button variant="outline" size="sm" asChild>
                <Link to={`/projects/${projectId}/scenarios`}>
                  Create scenario
                </Link>
              </Button>
            )}
          </div>
        </div>
      )}

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="live-walkthrough-persona">Persona</Label>
          <Select value={personaId} onValueChange={setPersonaId}>
            <SelectTrigger id="live-walkthrough-persona">
              <SelectValue placeholder="Select a persona..." />
            </SelectTrigger>
            <SelectContent>
              {personas?.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  <span className="flex flex-col">
                    <span>
                      {p.name} — {p.role}
                    </span>
                    {p.brief && (
                      <span className="text-xs text-muted-foreground">
                        {p.brief}
                      </span>
                    )}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="live-walkthrough-scenario">Scenario</Label>
          <Select value={scenarioId} onValueChange={setScenarioId}>
            <SelectTrigger id="live-walkthrough-scenario">
              <SelectValue placeholder="Select a scenario..." />
            </SelectTrigger>
            <SelectContent>
              {scenarios?.map((s) => {
                const sa = s as {
                  id: string;
                  outlineName?: string | null;
                  name?: string | null;
                  personaName?: string | null;
                };
                const label = sa.outlineName ?? sa.name ?? "Scenario";
                return (
                  <SelectItem key={sa.id} value={sa.id}>
                    {label}
                    {sa.personaName ? ` — ${sa.personaName}` : ""}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="live-walkthrough-target-url">Target URL</Label>
          <Input
            id="live-walkthrough-target-url"
            type="url"
            placeholder="https://example.com"
            value={targetUrl}
            onChange={(e) => setTargetUrl(e.target.value)}
          />
          <p className="text-sm text-muted-foreground">
            The URL the agent will navigate to and walk through
          </p>
        </div>

        <div className="space-y-3">
          <Label>Analysis lenses</Label>
          <p className="text-sm text-muted-foreground">
            Choose which perspectives to analyze the walkthrough from. The
            persona navigates; additional lenses analyze the captured material
            afterward.
          </p>
          <div className="space-y-2">
            {LENS_OPTIONS.map((lens) => {
              const availability = lensAvailability?.find(
                (l) => l.id === lens.id,
              );
              const isAvailable = lens.alwaysEnabled || availability?.available;
              const isChecked = selectedLenses.includes(lens.id);
              const LensIcon = lens.icon;

              return (
                <label
                  key={lens.id}
                  className={`flex items-start gap-3 rounded-lg border p-3 transition-colors ${
                    !isAvailable
                      ? "cursor-not-allowed bg-muted/30"
                      : isChecked
                        ? "border-primary/50 bg-primary/5 cursor-pointer"
                        : "cursor-pointer hover:bg-accent/50"
                  }`}
                >
                  <Checkbox
                    checked={isChecked}
                    disabled={lens.alwaysEnabled || !isAvailable}
                    onCheckedChange={(checked) => {
                      if (lens.alwaysEnabled) return;
                      setSelectedLenses((prev) =>
                        checked
                          ? [...prev, lens.id]
                          : prev.filter((l) => l !== lens.id),
                      );
                    }}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <LensIcon className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">{lens.label}</span>
                      {lens.alwaysEnabled && (
                        <span className="text-xs text-muted-foreground">
                          (always on)
                        </span>
                      )}
                    </div>
                    <p className="mt-0.5 text-sm text-muted-foreground">
                      {lens.description}
                    </p>
                    {!isAvailable && availability?.reason && (
                      <p className="mt-1 flex items-center gap-1 text-sm text-severity-important">
                        <Info className="h-3 w-3" aria-hidden="true" />
                        {availability.reason}
                      </p>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      </div>

      <Button
        onClick={handleStart}
        disabled={loading || !personaId || !scenarioId || !targetUrl}
      >
        {loading ? "Preparing instructions..." : "Prepare agent walkthrough"}
      </Button>
    </div>
  );
}
